/* <lunched-calendar> - keeps the week dots in sync with the scroll position.
 *
 * Observed attributes: none.
 * Dispatched events:   none.
 * Consumed events:     none.
 *
 * Week navigation itself is pure HTML: prev/next and every dot are in-page
 * anchors pointing at sibling week panels inside a scroll-snap track, so the
 * calendar is fully navigable with no JavaScript. This element only:
 *   1. scrolls the current week into view on load (without moving the page,
 *      which a bare :target anchor would do), and
 *   2. marks the dot for whichever week is actually in view.
 */

class LunchedCalendar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-weeks]');
    if (!this.track) return;

    this.weeks = Array.from(this.querySelectorAll('[data-week]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    if (!this.weeks.length) return;

    this._scrollToCurrent();

    if (!this.dots.length) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this._setActive(entry.target.dataset.week);
        });
      },
      { root: this.track, threshold: 0.55 }
    );
    this.weeks.forEach((w) => this.observer.observe(w));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /* Bring the server-marked current week into view. Uses scrollLeft rather than
     scrollIntoView so the surrounding page never jumps. Skipped when the URL
     already targets a specific week, so a shared deep link still wins. */
  _scrollToCurrent() {
    if (window.location.hash) return;
    const current = this.weeks.find((w) => w.classList.contains('is-current'));
    if (!current || current === this.weeks[0]) return;
    this.track.scrollLeft = current.offsetLeft - this.track.offsetLeft;
  }

  _setActive(weekKey) {
    if (!weekKey) return;
    this.dots.forEach((dot) => {
      dot.classList.toggle('is-active', dot.dataset.dot === weekKey);
    });
  }
}

if (!customElements.get('lunched-calendar')) {
  customElements.define('lunched-calendar', LunchedCalendar);
}
